import React, { useState, useEffect, useRef } from 'react';
import { Sheet, SheetSnapshot, HistoryActionType, Pouch, Group, SheetHistoryEntry } from '../types';
import { HistoryLogic } from '../utils/HistoryLogic';
import { getModifierKeys } from '../App';
import { formatTooltip } from '../utils/tooltipFormatter';
import './styles/HistoryComponent.scss';

interface HistoryComponentProps {
  sheet: Sheet;
  pouches: Pouch[];
  groups: Group[];
  onStateUpdate: (newSheet: Sheet, newPouches: Pouch[], newGroups: Group[]) => void;
  isOpen?: boolean; // right-sidebarの開閉状態
}

const HistoryComponent: React.FC<HistoryComponentProps> = ({
  sheet,
  pouches,
  groups,
  onStateUpdate,
  isOpen = true
}) => {
  // シートの履歴配列が未定義の場合は空配列を使用
  const history = sheet.history || [];
  const snapshots = sheet.snapshots || [];
  const [isSnapshotMenuOpen, setIsSnapshotMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // IME入力中かどうかの状態（スナップショットIDをキーとして管理）
  const [composingSnapshots, setComposingSnapshots] = useState<Set<string>>(new Set());
  
  // undoredo用のタイマーref
  const undoRedoTimerRef = useRef<number | null>(null);
  
  // スライドショー用の状態とタイマー
  const [isSlideshowMode, setIsSlideshowMode] = useState(false);
  const [slideshowDirection, setSlideshowDirection] = useState<'undo' | 'redo' | null>(null);
  const slideshowTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const currentSheetRef = useRef<Sheet>(sheet);
  const currentPouchesRef = useRef<Pouch[]>(pouches);
  const currentGroupsRef = useRef<Group[]>(groups);
  
  // スライドショー状態をrefで管理（タイマー内で確実に取得するため）
  const isSlideshowModeRef = useRef<boolean>(false);
  const slideshowDirectionRef = useRef<'undo' | 'redo' | null>(null);
  
  // history-percentageのアニメーション用状態とref
  const [animatedHistoryPercentage, setAnimatedHistoryPercentage] = useState(0);
  const historyPercentageAnimFrameRef = useRef<number | null>(null);
  const historyPercentageStartTimeRef = useRef<number | null>(null);
  const previousSheetIdRef = useRef<string | null>(null);
  
  // スライドショー中かどうかの状態
  const isSlideshowActive = isSlideshowMode && slideshowDirection !== null;
  
  // マウスダウン時のスライドショー状態を記録
  const mouseDownSlideshowStateRef = useRef<boolean>(false);
  // マウスダウン時にスライドショー中でなかったかどうかを記録
  const mouseDownWasNotSlideshowRef = useRef<boolean>(false);

  // undo/redo/slideshowクラスを追加する関数
  const addUndoRedoClass = (type: 'undo' | 'redo' | 'slideshow' = 'undo') => {
    // 既存のタイマーがあればクリア
    if (undoRedoTimerRef.current) {
      window.clearTimeout(undoRedoTimerRef.current);
      undoRedoTimerRef.current = null;
    }

    // スライドショー中かどうかを判定（refを使用して確実に取得）
    const isCurrentlySlideshowActive = isSlideshowModeRef.current && slideshowDirectionRef.current !== null;

    // 既存のクラスを削除
    if (isCurrentlySlideshowActive) {
      // スライドショー中：slideshowクラスは保持、undo/redoクラスのみ削除
      document.documentElement.classList.remove('undoredo', 'undo', 'redo');
    } else {
      // 通常時：すべてのクラスを削除
      document.documentElement.classList.remove('undoredo', 'undo', 'redo', 'slideshow');
    }

    // 共通のundoredoクラスと個別のクラスを追加
    document.documentElement.classList.add('undoredo', type);

    // 500ms後にクラスを削除するタイマーを設定
    undoRedoTimerRef.current = window.setTimeout(() => {
      if (isCurrentlySlideshowActive) {
        // スライドショー中：slideshowクラスは保持、undo/redoクラスのみ削除
        document.documentElement.classList.remove('undoredo', 'undo', 'redo');
      } else {
        // 通常時：すべてのクラスを削除
        document.documentElement.classList.remove('undoredo', 'undo', 'redo', 'slideshow');
      }
      undoRedoTimerRef.current = null;
    }, 500);
  };

  // スライドショーを開始する関数
  const startSlideshow = (direction: 'undo' | 'redo') => {
    // 既にスライドショー中なら何もしない
    if (isSlideshowMode && slideshowDirection !== null) {
      return;
    }
    
    // refを最新の状態に更新
    currentSheetRef.current = sheet;
    currentPouchesRef.current = pouches;
    currentGroupsRef.current = groups;
    
    setIsSlideshowMode(true);
    setSlideshowDirection(direction);
    // refも更新
    isSlideshowModeRef.current = true;
    slideshowDirectionRef.current = direction;
    // スライドショー用のクラスを追加
    addUndoRedoClass('slideshow');
    
    // モバイルの場合は右サイドバーを閉じる
    if (document.documentElement.classList.contains('mobile')) {
      const rightSidebar = document.querySelector('.right-sidebar');
      if (rightSidebar && !rightSidebar.classList.contains('closed')) {
        rightSidebar.classList.add('closed');
        rightSidebar.classList.remove('open');
        
      }
    }
    
    // 0.5秒ごとに実行
    slideshowTimerRef.current = window.setInterval(() => {
      // 現在の最新の状態を取得
      const currentSheet = currentSheetRef.current;
      
      // スライドショー状態をrefで管理して確実に取得
      const currentSlideshowMode = isSlideshowModeRef.current;
      const currentSlideshowDirection = slideshowDirectionRef.current;
      
      if (direction === 'undo') {
        const result = HistoryLogic.undo(currentSheet);
        if (result) {
          addUndoRedoClass('undo');
          
          // クラスが確実に適用されるのを待ってから実際のundo処理を実行
          // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // refを更新
              currentSheetRef.current = result.newSheet;
              currentPouchesRef.current = result.newPouches;
              currentGroupsRef.current = result.newGroups;
              onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
            });
          });
          
          // 末端に達したら停止
          if (checkSlideshowEnd(result.newSheet, direction)) {
            // 最後のundo/redoが始まってから1.5秒後に停止
            setTimeout(() => {
              stopSlideshow();
            }, 1500);
          }
        } else {
          // HistoryLogicがnullを返した場合（末端到達）
          stopSlideshow();
        }
      } else {
        const result = HistoryLogic.redo(currentSheet);
        if (result) {
          addUndoRedoClass('redo');
          
          // クラスが確実に適用されるのを待ってから実際のredo処理を実行
          // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // refを更新
              currentSheetRef.current = result.newSheet;
              currentPouchesRef.current = result.newPouches;
              currentGroupsRef.current = result.newGroups;
              onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
            });
          });
          
          // 末端に達したら停止
          if (checkSlideshowEnd(result.newSheet, direction)) {
            // 最後のundo/redoが始まってから1.5秒後に停止
            setTimeout(() => {
              stopSlideshow();
            }, 1500);
          }
        } else {
          // HistoryLogicがnullを返した場合（末端到達）
          stopSlideshow();
        }
      }
    }, 500);
  };

  // スライドショーを停止する関数
  const stopSlideshow = () => {
    setIsSlideshowMode(false);
    setSlideshowDirection(null);
    // refも更新
    isSlideshowModeRef.current = false;
    slideshowDirectionRef.current = null;
    // スライドショー用のクラスを削除
    document.documentElement.classList.remove('undoredo', 'undo', 'redo', 'slideshow');
    
    // スライドショータイマーをクリア
    if (slideshowTimerRef.current) {
      window.clearInterval(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }
    
    // 長押しタイマーもクリア
    if (longPressTimerRef.current) {
      
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // history関連のrefをリセット
    mouseDownSlideshowStateRef.current = false;
    mouseDownWasNotSlideshowRef.current = false;
    
    // refを最新の状態に更新
    currentSheetRef.current = sheet;
    currentPouchesRef.current = pouches;
    currentGroupsRef.current = groups;
  };

  // 長押し検出の関数
  const handleLongPress = (direction: 'undo' | 'redo') => {
    // スライドショー中は長押し検出を無効
    if (isSlideshowActive) {
      return;
    }
    
    // 既存のタイマーがあればクリア
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    
    longPressTimerRef.current = window.setTimeout(() => {
      startSlideshow(direction);
      longPressTimerRef.current = null;
    }, 500);
  };

  // マウスダウン時の処理
  const handleMouseDown = (direction: 'undo' | 'redo') => {
    // タッチデバイスではマウスイベントを無視
    if ('ontouchstart' in window) {
      return;
    }
    
    // 長押しタイマーの存在でスライドショー状態を判断
    mouseDownSlideshowStateRef.current = !!longPressTimerRef.current;
    // マウスダウン時にスライドショー中でなかったかどうかを記録
    mouseDownWasNotSlideshowRef.current = !isSlideshowActive;
    
    handleLongPress(direction);
  };

  // タッチスタート時の処理
  const handleTouchStart = (direction: 'undo' | 'redo') => {
    // 長押しタイマーの存在でスライドショー状態を判断
    mouseDownSlideshowStateRef.current = !!longPressTimerRef.current;
    // タッチスタート時にスライドショー中でなかったかどうかを記録
    mouseDownWasNotSlideshowRef.current = !isSlideshowActive;
    
    handleLongPress(direction);
  };

  // マウスアップ時の処理
  const handleMouseUp = () => {
    // タッチデバイスではマウスイベントを無視
    if ('ontouchstart' in window) {
      return;
    }
    
    // スライドショー中は何もしない
    if (isSlideshowActive) {
      return;
    }
    // マウスダウン時に長押しタイマーが存在しなかった場合のみキャンセル
    if (!mouseDownSlideshowStateRef.current) {
      cancelLongPress();
    }
  };

  // タッチエンド時の処理
  const handleTouchEnd = (direction: 'undo' | 'redo') => {
    // 長押しからスライドショーが開始された場合は無視
    if (mouseDownWasNotSlideshowRef.current && isSlideshowActive && slideshowDirection === direction) {
      return;
    }
    
    // スライドショー中の一時停止処理
    if (isSlideshowActive && slideshowDirection === direction) {
      handleSlideshowPause();
      return;
    }
    
    // マウスダウン時に長押しタイマーが存在しなかった場合（短押しの場合）
    if (!mouseDownSlideshowStateRef.current) {
      cancelLongPress();
      // 短押しの場合は通常のundo/redoを実行
      if (direction === 'undo') {
        handleUndo();
      } else {
        handleRedo();
      }
    } else {
      cancelLongPress();
    }
  };

  // 長押しキャンセルの関数
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (undoRedoTimerRef.current) {
        window.clearTimeout(undoRedoTimerRef.current);
        undoRedoTimerRef.current = null;
      }
      if (slideshowTimerRef.current) {
        window.clearInterval(slideshowTimerRef.current);
        slideshowTimerRef.current = null;
      }
      if (longPressTimerRef.current) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, []);

  // propsが更新されたときにrefも更新
  useEffect(() => {
    currentSheetRef.current = sheet;
    currentPouchesRef.current = pouches;
    currentGroupsRef.current = groups;
  }, [sheet, pouches, groups]);

  // プラットフォーム判定関数
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const getAltControlModifierKey = (e: any) => {
    return isMac ? e.ctrlKey : e.altKey;
  };

  // キーボードショートカットの処理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      
      // Cmd/Ctrl+Z で Undo（Controlキーが押されていない場合のみ）
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && !e.shiftKey && !getAltControlModifierKey(e)) {
        e.preventDefault();
        // 現在フォーカスされているテキストボックスをblur
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
          (document.activeElement as HTMLElement).blur();
        }
        handleUndo();
      }
      // Cmd/Ctrl+Shift+Z で Redo（標準的なredo、Controlキーが押されていない場合のみ）
      else if (((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && e.shiftKey && !getAltControlModifierKey(e)) || 
          (e.ctrlKey && e.key === 'y' && !e.metaKey)) {
        e.preventDefault();
        // 現在フォーカスされているテキストボックスをblur
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
          (document.activeElement as HTMLElement).blur();
        }
        handleRedo();
      }
      // Cmd/Ctrl+Alt+Z で Alt+Undo（履歴エントリを削除してundo）
      else if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && getAltControlModifierKey(e) && !e.shiftKey) {
        e.preventDefault();
        // 現在フォーカスされているテキストボックスをblur
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
          (document.activeElement as HTMLElement).blur();
        }
        handleShiftUndo();
      }
      // Cmd/Ctrl+Alt+Shift+Z で Alt+Redo（履歴エントリを削除してredo）
      else if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && getAltControlModifierKey(e) && e.shiftKey) {
        e.preventDefault();
        // 現在フォーカスされているテキストボックスをblur
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
          (document.activeElement as HTMLElement).blur();
        }
        handleShiftRedo();
      }
      // Cmd/Ctrl+Alt+Y で Alt+Redo（履歴エントリを削除してredo）
      else if ((e.metaKey || e.ctrlKey) && e.key === 'y' && getAltControlModifierKey(e)) {
        e.preventDefault();
        // 現在フォーカスされているテキストボックスをblur
        if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
          (document.activeElement as HTMLElement).blur();
        }
        handleShiftRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sheet]); // sheetが変更されたときに再登録

  const handleUndo = () => {
    const result = HistoryLogic.undo(sheet);
    if (result) {
      // undoredoクラスを追加
      addUndoRedoClass('undo');
      
      // クラスが確実に適用されるのを待ってから実際のundo処理を実行
      // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // 現在のエントリのタイプを取得して渡す（元に戻す操作のため）
          const currentActionType = sheet.history[sheet.historyIndex]?.type;
          onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
        });
      });
    }
  };

  const handleRedo = () => {
    const result = HistoryLogic.redo(sheet);
    if (result) {
      // undoredoクラスを追加
      addUndoRedoClass('redo');
      
      // クラスが確実に適用されるのを待ってから実際のredo処理を実行
      // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // 次のエントリのタイプを取得して渡す（やり直し操作のため）
          const nextActionType = sheet.history[sheet.historyIndex + 1]?.type;
          onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
        });
      });
    }
  };

  const handleShiftUndo = () => {
    const result = HistoryLogic.shiftUndo(sheet);
    if (result) {
      // undoredoクラスを追加
      addUndoRedoClass('undo');
      
      // クラスが確実に適用されるのを待ってから実際のshift+undo処理を実行
      // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
        });
      });
    }
  };

  const handleShiftRedo = () => {
    const result = HistoryLogic.shiftRedo(sheet);
    if (result) {
      // undoredoクラスを追加
      addUndoRedoClass('redo');
      
      // クラスが確実に適用されるのを待ってから実際のshift+redo処理を実行
      // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
        });
      });
    }
  };

  // undoボタンのクリックハンドラー（GroupComponentと同じ方式）
  const handleUndoClick = (e: React.MouseEvent) => {
    if (getAltControlModifierKey(e)) {
      e.stopPropagation();
      handleShiftUndo();
    } else {
      handleClick('undo');
    }
  };

  // redoボタンのクリックハンドラー（GroupComponentと同じ方式）
  const handleRedoClick = (e: React.MouseEvent) => {
    if (getAltControlModifierKey(e)) {
      e.stopPropagation();
      handleShiftRedo();
    } else {
      handleClick('redo');
    }
  };

  // スライドショー中の一時停止処理
  const handleSlideshowPause = () => {
    // undo/redoは即座に停止
    if (slideshowTimerRef.current) {
      window.clearInterval(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }
    
    // slideshowクラスは1.5秒後に削除
    setTimeout(() => {
      setIsSlideshowMode(false);
      setSlideshowDirection(null);
      // refも更新
      isSlideshowModeRef.current = false;
      slideshowDirectionRef.current = null;
      document.documentElement.classList.remove('slideshow');
      
      // refを最新の状態に更新
      currentSheetRef.current = sheet;
      currentPouchesRef.current = pouches;
      currentGroupsRef.current = groups;
    }, 1500);
  };

  // クリック処理（スライドショー開始前の長押しの場合は無効）
  const handleClick = (direction: 'undo' | 'redo') => {
    // マウスダウン時にスライドショー中でなかった場合は、長押しからスライドショーが開始された可能性がある
    if (mouseDownWasNotSlideshowRef.current && isSlideshowActive && slideshowDirection === direction) {
      return;
    }
    
    // 通常のクリック処理
    if (isSlideshowActive && slideshowDirection === direction) {
      handleSlideshowPause();
    } else if (direction === 'undo') {
      handleUndo();
    } else {
      handleRedo();
    }
  };

  // スライドショー中に末端に達したかチェックする関数
  const checkSlideshowEnd = (newSheet: Sheet, direction: 'undo' | 'redo') => {
    if (direction === 'undo' && newSheet.historyIndex <= 0) {
      return true;
    }
    if (direction === 'redo' && newSheet.historyIndex >= newSheet.history.length - 1) {
      return true;
    }
    return false;
  };

  const handleCreateSnapshot = () => {
    const name = `Snapshot ${(sheet.snapshots?.length || 0) + 1}`;
    const description = `Created at ${new Date().toLocaleString()}`;
    const updatedSheet = HistoryLogic.createSnapshot(sheet, pouches, groups, name, description);
    onStateUpdate(updatedSheet, pouches, groups);
  };

  const handleRestoreSnapshot = (snapshotId: string) => {
    const result = HistoryLogic.restoreSnapshot(sheet, snapshotId);
    if (result) {
      // undoredoクラスを追加してアニメーション効果を適用
      addUndoRedoClass('redo');
      
      // クラスが確実に適用されるのを待ってから実際の復元処理を実行
      // 複数のrequestAnimationFrameを使用してより確実にクラス適用を待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onStateUpdate(result.newSheet, result.newPouches, result.newGroups);
        });
      });
    }
  };

  const handleUpdateSnapshotName = (snapshotId: string, newName: string) => {
    const updatedSheet = HistoryLogic.updateSnapshotName(sheet, snapshotId, newName);
    onStateUpdate(updatedSheet, pouches, groups);
  };

  const handleOverwriteSnapshot = (snapshotId: string) => {
    const updatedSheet = HistoryLogic.overwriteSnapshot(sheet, pouches, groups, snapshotId);
    onStateUpdate(updatedSheet, pouches, groups);
  };

  const handleDeleteSnapshot = (snapshotId: string) => {
    const updatedSheet = HistoryLogic.deleteSnapshot(sheet, snapshotId);
    onStateUpdate(updatedSheet, pouches, groups);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 履歴エントリの説明を整形する関数
  const formatHistoryDescription = (entry: SheetHistoryEntry | undefined) => {
    if (!entry) return '';
    
    // targetIdがない場合は説明をそのまま返す
    return entry.description;
  };

  // history-indexの変更を監視してCSSカスタムプロパティをアニメーション更新
  useEffect(() => {
    const targetHistoryPercentage = history.length > 0 ? (sheet.historyIndex / (history.length - 1)) * 100 : 0;
    
    // 現在進行中のアニメーションをキャンセル
    if (historyPercentageAnimFrameRef.current) {
      cancelAnimationFrame(historyPercentageAnimFrameRef.current);
      historyPercentageAnimFrameRef.current = null;
    }

    // シート切替時はアニメーション無しで即座に設定
    const isSheetSwitch = previousSheetIdRef.current !== null && previousSheetIdRef.current !== sheet.id;
    
    if (isSheetSwitch) {
      setAnimatedHistoryPercentage(targetHistoryPercentage);
      document.documentElement.style.setProperty('--history-percentage', `${targetHistoryPercentage}%`);
      previousSheetIdRef.current = sheet.id;
      
      // シート切替時にhistory関連のrefをリセット
      mouseDownSlideshowStateRef.current = false;
      mouseDownWasNotSlideshowRef.current = false;
      return;
    }
    
    // 初回ロード時も即座に設定
    if (previousSheetIdRef.current === null) {
      setAnimatedHistoryPercentage(targetHistoryPercentage);
      document.documentElement.style.setProperty('--history-percentage', `${targetHistoryPercentage}%`);
      previousSheetIdRef.current = sheet.id;
      
      // 初回ロード時もhistory関連のrefをリセット
      mouseDownSlideshowStateRef.current = false;
      mouseDownWasNotSlideshowRef.current = false;
      return;
    }

    // スライドショー中はアニメーションをスキップして即座に設定
    // if (isSlideshowActive) {
    //   setAnimatedHistoryPercentage(targetHistoryPercentage);
    //   document.documentElement.style.setProperty('--history-percentage', `${targetHistoryPercentage}%`);
    //   return;
    // }

    // アニメーション開始値
    const startPercentage = animatedHistoryPercentage;
    const endPercentage = targetHistoryPercentage;

    // 同じ値の場合はアニメーションしない
    if (startPercentage === endPercentage) {
      return;
    }

    // アニメーション期間（500ms）
    const duration = isSheetSwitch ? 5 : 500;

    // アニメーション関数
    const animateHistoryPercentage = (timestamp: number) => {
      if (!historyPercentageStartTimeRef.current) {
        historyPercentageStartTimeRef.current = timestamp;
      }

      const elapsed = timestamp - historyPercentageStartTimeRef.current;
      
      // アニメーション終了
      if (elapsed >= duration) {
        setAnimatedHistoryPercentage(endPercentage);
        document.documentElement.style.setProperty('--history-percentage', `${endPercentage}%`);
        historyPercentageAnimFrameRef.current = null;
        historyPercentageStartTimeRef.current = null;
        return;
      }

      // 進捗率を計算（0～1）
      const progress = elapsed / duration;
      
      // 現在の値を計算（線形補間）
      const currentPercentage = startPercentage + (endPercentage - startPercentage) * progress;
      
      // 状態を更新
      setAnimatedHistoryPercentage(currentPercentage);
      document.documentElement.style.setProperty('--history-percentage', `${currentPercentage}%`);
      
      // 次のフレームを要求
      historyPercentageAnimFrameRef.current = requestAnimationFrame(animateHistoryPercentage);
    };

    // アニメーション開始
    historyPercentageStartTimeRef.current = null;
    historyPercentageAnimFrameRef.current = requestAnimationFrame(animateHistoryPercentage);
  }, [sheet.historyIndex, history.length, isSlideshowActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsSnapshotMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // setUndoRedoClassイベントリスナー
  useEffect(() => {
    const handleSetUndoRedoClass = (event: CustomEvent) => {
      const { type } = event.detail;
      addUndoRedoClass(type);
    };

    document.addEventListener('setUndoRedoClass', handleSetUndoRedoClass as EventListener);
    return () => {
      document.removeEventListener('setUndoRedoClass', handleSetUndoRedoClass as EventListener);
    };
  }, []);

  return (
    <div className="history-controls">
      <div className="history-buttons">
        <div className="history-description undo-description">
          {sheet.historyIndex > 0 && (
            formatHistoryDescription(history[sheet.historyIndex - 0])
          )}
        </div>
        <button 
          className={`history-button undo-button ${isSlideshowActive && slideshowDirection === 'undo' ? 'pause-button' : ''}`}
          data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: Undo\n${getModifierKeys().ctrlOption}+Click: Delete Current History & Undo\nLong Press: Slide Show (Reverse)`)}
          tabIndex={-1}
          onClick={handleUndoClick}
          onMouseDown={(e) => {
            // Control+クリックのデフォルト動作を防ぐ
            if (e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
            } else {
              // 通常のマウスダウンで長押し検出を開始
              handleMouseDown('undo');
            }
          }}
          onMouseUp={(e) => {
            // Control+クリックの場合は手動でクリック処理を実行
            if (e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
              handleUndoClick(e);
            } else {
              // 通常のマウスアップで長押しキャンセル処理
              handleMouseUp();
            }
          }}
          onMouseLeave={() => {
            // タッチデバイスではマウスイベントを無視
            if ('ontouchstart' in window) {
              return;
            }
            
            // スライドショー中は何もしない
            if (isSlideshowActive) {
              return;
            }
            if (!mouseDownSlideshowStateRef.current) {
              cancelLongPress();
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleTouchStart('undo');
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleTouchEnd('undo');
          }}
          onContextMenu={(e) => {
            if (getAltControlModifierKey(e)) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          disabled={!sheet.history || sheet.history.length <= 0 || isNaN(sheet.history.length) || (sheet.historyIndex <= 0 && !isSlideshowActive)}
        >
          {isSlideshowActive && slideshowDirection === 'undo' ? (
            <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 10h10a5 5 0 0 1 5 5v2"></path>
              <path d="M3 10l6 6"></path>
              <path d="M3 10l6-6"></path>
            </svg>
          )}
        </button>
        
        <button 
          className="history-button snapshot-button"
          tabIndex={-1}
          onClick={handleCreateSnapshot}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCreateSnapshot();
          }}
          data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Add Snapshot")}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
            <circle cx="12" cy="13" r="4"></circle>
          </svg>
        </button>
        
        <button 
          className={`history-button redo-button ${isSlideshowActive && slideshowDirection === 'redo' ? 'pause-button' : ''}`}
          data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: Redo\n${getModifierKeys().ctrlOption}+Click: Delete Current History & Redo\nLong Press: Slide Show (Forward)`)}
          tabIndex={-1}
          onClick={handleRedoClick}
          onMouseDown={(e) => {
            // Control+クリックのデフォルト動作を防ぐ
            if (e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
            } else {
              // 通常のマウスダウンで長押し検出を開始
              handleMouseDown('redo');
            }
          }}
          onMouseUp={(e) => {
            // Control+クリックの場合は手動でクリック処理を実行
            if (e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
              handleRedoClick(e);
            } else {
              // 通常のマウスアップで長押しキャンセル処理
              handleMouseUp();
            }
          }}
          onMouseLeave={() => {
            // タッチデバイスではマウスイベントを無視
            if ('ontouchstart' in window) {
              return;
            }
            
            // スライドショー中は何もしない
            if (isSlideshowActive) {
              return;
            }
            if (!mouseDownSlideshowStateRef.current) {
              cancelLongPress();
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleTouchStart('redo');
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleTouchEnd('redo');
          }}
          onContextMenu={(e) => {
            if (getAltControlModifierKey(e)) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          disabled={!sheet.history || sheet.history.length <= 0 || isNaN(sheet.history.length) || (sheet.historyIndex >= history.length - 1 && !isSlideshowActive)}
        >
          {isSlideshowActive && slideshowDirection === 'redo' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10H11a5 5 0 0 0-5 5v2"></path>
              <path d="M21 10l-6 6"></path>
              <path d="M21 10l-6-6"></path>
            </svg>
          )}
        </button>
        <div className="history-description redo-description">
          {sheet.historyIndex < history.length - 1 && (
            formatHistoryDescription(history[sheet.historyIndex + 1])
          )}
        </div>
      </div>
      <div className="snapshot-menu" ref={menuRef}>
        <div className="snapshot-list">
          {/* 現在の状態を一度だけ正規化（パフォーマンス最適化） */}
          {(() => {
            // Pouchを比較用に正規化（必要なプロパティのみ、決まった順序で抽出）
            const normalizePouches = (pouchesList: Pouch[]) => {
              return pouchesList.map(p => ({
                id: p.id,
                value: p.value,
                title: p.title || '',
                multiplier: p.multiplier,
                x: p.x,
                y: p.y,
                isNegative: p.isNegative,
                zIndex: p.zIndex || 0,
                color: p.color,
                isGroupTotalPouch: p.isGroupTotalPouch || false,
                referencedGroupId: p.referencedGroupId || '',
                oppositeToTotal: p.oppositeToTotal || false,
                lastExpression: p.lastExpression || '',
                valueWhenLastExpression: p.valueWhenLastExpression || 0,
                multiplyWhenLastExpression: p.multiplyWhenLastExpression || 1,
                isTime: p.isTime || false
              })).sort((a, b) => a.id.localeCompare(b.id)); // IDでソート
            };
            
            // Groupを比較用に正規化
            const normalizeGroups = (groupsList: Group[]) => {
              return groupsList.map(g => ({
                id: g.id,
                path: g.path,
                pouches: [...(g.pouches || [])].sort(), // パウチIDをソート
                color: g.color || '',
                originalColor: g.originalColor || '',
                title: g.title || '',
                placeholderText: g.placeholderText || '',
                zIndex: g.zIndex || 0
              })).sort((a, b) => a.id.localeCompare(b.id)); // IDでソート
            };
            
            const normalizedCurrentPouches = normalizePouches(pouches);
            const normalizedCurrentGroups = normalizeGroups(groups);
            const cleanCurrentPouchesStr = JSON.stringify(normalizedCurrentPouches);
            const cleanCurrentGroupsStr = JSON.stringify(normalizedCurrentGroups);
            
            return (sheet.snapshots || []).map(snapshot => {
            // ミュートグループに所属するパウチIDを取得
            const muteGroupPouchIds = new Set(
              (snapshot.groups || [])
                .filter(g => g.color === 'mute')
                .flatMap(g => g.pouches || [])
            );
            
            const snapshotTotal = snapshot.pouches
              .filter(p => !muteGroupPouchIds.has(p.id)) // ミュートグループのパウチを除外
              .reduce(
                (sum, p) => {
                  // 乗数が負の場合は割り算として計算
                  // valueには既にisNegativeが反映されているので、isNegativeを掛けない
                  const effectiveValue = p.multiplier < 0 
                    ? p.value / Math.abs(p.multiplier)
                    : p.value * p.multiplier;
                  
                  return sum + effectiveValue;
                },
                0
              );

              // 現在の状態とスナップショットの状態が同じかどうかをチェック
              // スナップショットも同じ方法で正規化して比較
              const normalizedSnapshotPouches = normalizePouches(snapshot.pouches);
              const normalizedSnapshotGroups = normalizeGroups(snapshot.groups);
              
              const isCurrentState = cleanCurrentPouchesStr === JSON.stringify(normalizedSnapshotPouches) &&
                                      cleanCurrentGroupsStr === JSON.stringify(normalizedSnapshotGroups);

              return (
                <div 
                  key={snapshot.id}
                  className={`snapshot-item${isCurrentState ? ' current' : ''}`}
                  onClick={() => handleRestoreSnapshot(snapshot.id)}
                >
                  <div className="snapshot-item-content">
                    <div className="snapshot-title" data-tooltip-id="tt" data-tooltip-html={[snapshot.name ? `<b>${snapshot.name}</b>` : '', formatTooltip('Click: Edit Snapshot Title')].filter(Boolean).join('<br/>')}>
                      <input
                        type="text"
                        placeholder="No Title"
                        tabIndex={isOpen ? 4000 : -1}
                        value={snapshot.name}
                        onChange={(e) => handleUpdateSnapshotName(snapshot.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // モバイル環境では明示的にフォーカスを設定
                          (e.target as HTMLInputElement).focus();
                        }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          // IME入力中は何もしない（確定を待つ）
                          if (composingSnapshots.has(snapshot.id)) {
                            return;
                          }
                          
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            setTimeout(() => {
                              (e.target as HTMLInputElement).blur();
                            }, 10);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            // 元の値に戻す
                            const updatedSheet = HistoryLogic.updateSnapshotName(sheet, snapshot.id, snapshot.name);
                            onStateUpdate(updatedSheet, pouches, groups);
                            setTimeout(() => {
                              (e.target as HTMLInputElement).blur();
                            }, 10);
                          }
                        }}
                        onCompositionStart={() => {
                          setComposingSnapshots(prev => new Set(prev).add(snapshot.id));
                        }}
                        onCompositionEnd={() => {
                          setComposingSnapshots(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(snapshot.id);
                            return newSet;
                          });
                        }}
                      />
                    </div>
                    <div className="snapshot-total-date" data-tooltip-id="tt" data-tooltip-html={[snapshot.name ? `<b>${snapshot.name}</b>` : '', formatTooltip('Click: Restore Snapshot')].filter(Boolean).join('<br/>')}>
                      <span className={`snapshot-total calcval ${snapshotTotal < 0 ? 'negative' : ''}`}>
                        {snapshotTotal < 0 ? '-' : ''}{Math.abs(snapshotTotal).toLocaleString()}
                      </span>
                      <br/>
                      {formatDate(new Date(snapshot.timestamp))}
                    </div>
                  </div>
                  <div className="snapshot-buttons">
                    <button
                      className="restore-button"
                      tabIndex={-1}
                      onClick={() => handleRestoreSnapshot(snapshot.id)}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRestoreSnapshot(snapshot.id);
                      }}
                      data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Restore Snapshot")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 10h10a5 5 0 0 1 5 5v2"></path>
                        <path d="M3 10l6 6"></path>
                        <path d="M3 10l6-6"></path>
                      </svg>
                    </button>
                    <button
                      className="overwrite-button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOverwriteSnapshot(snapshot.id);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOverwriteSnapshot(snapshot.id);
                      }}
                      data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Overwrite Snapshot")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                    </button>
                    <button
                      className="delete-button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSnapshot(snapshot.id);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteSnapshot(snapshot.id);
                      }}
                      data-tooltip-id="tt" data-tooltip-html={formatTooltip("Click: Delete Snapshot")}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
};

export default HistoryComponent; 